import type { AcousticCalibrationSnapshot, AcousticCalibrationTag } from './calibrationTypes';
import { ACOUSTIC_CALIBRATION_TAGS } from './calibrationTypes';
import type { AcousticThresholdOverrides } from './acousticThresholdOverrides';
import {
  buildCategoryContinua,
  continuumCentroidLowerThreshold,
  continuumLowerThreshold,
  continuumUpperThreshold,
  getContinuumForTag,
  getOtherContinua,
  metricStats,
  otherMetricStats,
  type CategoryContinuum,
  type ContinuumMetricKey
} from './calibrationCategoryContinuum';
import { ACOUSTIC_FACTORY_DEFAULTS } from './config';
import { clamp01 } from './utils';

const MIN_SNAPSHOTS = 2;
const SILENCE_BASELINE_GAP = 0.04;

export type ThresholdDerivationResult =
  | {
      ok: true;
      tag: AcousticCalibrationTag;
      tagLabel: string;
      snapshotCount: number;
      overrides: AcousticThresholdOverrides;
      summary: string[];
      continua: CategoryContinuum[];
      targetContinuum: CategoryContinuum;
    }
  | { ok: false; error: string };

export function deriveThresholdOverridesForTag(
  tag: AcousticCalibrationTag,
  allSnapshots: AcousticCalibrationSnapshot[]
): ThresholdDerivationResult {
  const positive = allSnapshots.filter((snap) => snap.tag === tag);
  if (positive.length < MIN_SNAPSHOTS) {
    return {
      ok: false,
      error: `Mindestens ${MIN_SNAPSHOTS} Snapshots mit Tag „${tag}" nötig (aktuell: ${positive.length}).`
    };
  }

  const continua = buildCategoryContinua(allSnapshots);
  const targetContinuum = getContinuumForTag(continua, tag);
  if (!targetContinuum) {
    return { ok: false, error: 'Keine Kontinuum-Daten für diese Kategorie.' };
  }

  const others = getOtherContinua(continua, tag);
  const tagMeta = ACOUSTIC_CALIBRATION_TAGS.find((entry) => entry.key === tag);
  const tagLabel = tagMeta?.label ?? tag;

  let overrides: AcousticThresholdOverrides;
  if (tag === 'babble') {
    overrides = deriveBabbleThresholds(targetContinuum, others);
  } else if (tag === 'shouting') {
    overrides = deriveShoutingThresholds(targetContinuum, others);
  } else if (tag === 'single_speaker') {
    overrides = deriveSingleSpeakerGuardThresholds(targetContinuum, others);
  } else if (tag === 'conversation') {
    overrides = deriveConversationThresholds(targetContinuum, others);
  } else {
    return { ok: false, error: `Für „${tagLabel}" sind noch keine automatischen Schwellen definiert.` };
  }

  return {
    ok: true,
    tag,
    tagLabel,
    snapshotCount: positive.length,
    overrides,
    summary: describeDerivation(targetContinuum, others, overrides),
    continua,
    targetContinuum
  };
}

/**
 * Rauschboden / Klassen-Hintergrund aus Stille-Snapshots.
 * Trennt „echtes“ Signal (Gemurmel, Sprache) vom Leerraum-Rauschen im Raum.
 */
export function deriveSilenceBaselineOverrides(
  allSnapshots: AcousticCalibrationSnapshot[]
): ThresholdDerivationResult {
  const positive = allSnapshots.filter((snap) => snap.tag === 'silence');
  if (positive.length < MIN_SNAPSHOTS) {
    return {
      ok: false,
      error: `Mindestens ${MIN_SNAPSHOTS} Stille-Snapshots nötig (aktuell: ${positive.length}).`
    };
  }

  const continua = buildCategoryContinua(allSnapshots);
  const targetContinuum = getContinuumForTag(continua, 'silence');
  if (!targetContinuum) {
    return { ok: false, error: 'Keine Kontinuum-Daten für Stille.' };
  }

  const noise = metricStats(targetContinuum, 'fusion_noise');
  const loud = metricStats(targetContinuum, 'fusion_loudness');
  const diff = metricStats(targetContinuum, 'fusion_diffuseness');

  const overrides: AcousticThresholdOverrides = {};
  const summary: string[] = [
    `Rauschboden „Stille" (${positive.length} Snapshots, min · Schwerpunkt · max):`
  ];

  if (noise) {
    summary.push(
      `  Rauschen: ${noise.min.toFixed(3)} · ${noise.mean.toFixed(3)} · ${noise.max.toFixed(3)}`
    );
    const floor = clamp01(noise.max + SILENCE_BASELINE_GAP);
    overrides.roomActivityNoiseMin = Number(floor.toFixed(3));
    overrides.babbleNoiseIntensityMin = Number(
      Math.max(ACOUSTIC_FACTORY_DEFAULTS.babbleNoiseIntensityMin, floor).toFixed(3)
    );
  }

  if (loud) {
    summary.push(
      `  Lautstärke: ${loud.min.toFixed(3)} · ${loud.mean.toFixed(3)} · ${loud.max.toFixed(3)}`
    );
    const floor = clamp01(loud.max + SILENCE_BASELINE_GAP);
    overrides.roomActivityLoudnessMin = Number(floor.toFixed(3));
    overrides.babbleLoudnessMin = Number(
      Math.max(ACOUSTIC_FACTORY_DEFAULTS.babbleLoudnessMin, floor).toFixed(3)
    );
  }

  if (diff) {
    summary.push(
      `  Diffusheit: ${diff.min.toFixed(3)} · ${diff.mean.toFixed(3)} · ${diff.max.toFixed(3)}`
    );
  }

  if (!Object.keys(overrides).length) {
    return { ok: false, error: 'Stille-Snapshots enthalten keine Rausch-/Lautstärke-Kennwerte.' };
  }

  summary.push('Neue Schwellen (über Hintergrund):');
  summary.push(
    ...Object.entries(overrides).map(([key, value]) => `  ${LABELS[key] ?? key}: ${value.toFixed(3)}`)
  );
  summary.push(
    'Hinweis: Situations-Schwellen (Gemurmel, Geschrei, …) brauchen zusätzlich je 2 Snapshots der jeweiligen Kategorie.'
  );

  return {
    ok: true,
    tag: 'silence',
    tagLabel: 'Stille',
    snapshotCount: positive.length,
    overrides,
    summary,
    continua,
    targetContinuum
  };
}

function requireStats(
  continuum: CategoryContinuum,
  others: CategoryContinuum[],
  key: ContinuumMetricKey
): { target: NonNullable<ReturnType<typeof metricStats>>; others: ReturnType<typeof otherMetricStats> } | null {
  const target = metricStats(continuum, key);
  if (!target) return null;
  return { target, others: otherMetricStats(others, key) };
}

function deriveBabbleThresholds(
  target: CategoryContinuum,
  others: CategoryContinuum[]
): AcousticThresholdOverrides {
  const diff = requireStats(target, others, 'fusion_diffuseness');
  const noise = requireStats(target, others, 'fusion_noise');
  const turn = requireStats(target, others, 'fusion_turnTaking');
  const loud = requireStats(target, others, 'fusion_loudness');
  const peaks = requireStats(target, others, 'fusion_peaks');
  const yamnet = requireStats(target, others, 'yamnet_babble');

  const overrides: AcousticThresholdOverrides = {};

  if (diff) {
    overrides.babbleDiffusenessMin = Number(
      continuumCentroidLowerThreshold(diff.target, diff.others).toFixed(3)
    );
  }
  if (noise) {
    overrides.babbleNoiseIntensityMin = Number(
      continuumCentroidLowerThreshold(noise.target, noise.others).toFixed(3)
    );
    overrides.roomActivityNoiseMin = Number(continuumLowerThreshold(noise.target, noise.others).toFixed(3));
  }
  if (turn) {
    overrides.babbleTurnTakingMin = Number(continuumLowerThreshold(turn.target, turn.others).toFixed(3));
  }
  if (loud) {
    overrides.babbleLoudnessMin = Number(continuumLowerThreshold(loud.target, loud.others).toFixed(3));
    overrides.babbleLoudnessMax = Number(
      continuumUpperThreshold(loud.target, loud.others, true).toFixed(3)
    );
  }
  if (peaks) {
    overrides.babblePeakDensityMax = Number(
      continuumUpperThreshold(peaks.target, peaks.others, false).toFixed(4)
    );
  }
  if (yamnet) {
    overrides.yamnetBabbleTriggerMin = Number(continuumLowerThreshold(yamnet.target, yamnet.others).toFixed(3));
  }

  return overrides;
}

function deriveShoutingThresholds(
  target: CategoryContinuum,
  others: CategoryContinuum[]
): AcousticThresholdOverrides {
  const loud = requireStats(target, others, 'fusion_loudness');
  const peaks = requireStats(target, others, 'fusion_peaks');
  const crest = requireStats(target, others, 'fusion_crest');
  const yamnet = requireStats(target, others, 'yamnet_shouting');

  const overrides: AcousticThresholdOverrides = {};

  if (loud) {
    overrides.shoutingLoudnessThreshold = Number(
      continuumCentroidLowerThreshold(loud.target, loud.others).toFixed(3)
    );
    overrides.shoutingLoudnessWithCrestMin = Number(continuumLowerThreshold(loud.target, loud.others).toFixed(3));
  }
  if (crest) {
    overrides.shoutingCrestFactorMin = Number(
      Math.max(2.5, continuumLowerThreshold(crest.target, crest.others)).toFixed(2)
    );
  }
  if (peaks) {
    overrides.shoutingPeakDensityMin = Number(continuumLowerThreshold(peaks.target, peaks.others).toFixed(4));
  }
  if (yamnet) {
    overrides.yamnetShoutingTriggerMin = Number(continuumLowerThreshold(yamnet.target, yamnet.others).toFixed(3));
  }

  return overrides;
}

function deriveSingleSpeakerGuardThresholds(
  target: CategoryContinuum,
  others: CategoryContinuum[]
): AcousticThresholdOverrides {
  const loud = requireStats(target, others, 'fusion_loudness');
  const diff = requireStats(target, others, 'fusion_diffuseness');
  const overrides: AcousticThresholdOverrides = {};

  const babbleOther = others.find((c) => c.tag === 'babble');
  if (loud && babbleOther) {
    const babbleLoud = babbleOther.metrics.fusion_loudness;
    if (babbleLoud) {
      overrides.babbleLoudnessMin = Number(
        clamp01(Math.max(babbleLoud.min - 0.02, loud.target.max + 0.03)).toFixed(3)
      );
    }
  } else if (loud) {
    overrides.babbleLoudnessMin = Number(clamp01(loud.target.max + 0.04).toFixed(3));
  }

  if (diff) {
    overrides.babbleDiffusenessMin = Number(clamp01(diff.target.max + 0.05).toFixed(3));
  }

  return overrides;
}

function deriveConversationThresholds(
  target: CategoryContinuum,
  others: CategoryContinuum[]
): AcousticThresholdOverrides {
  const turn = requireStats(target, others, 'fusion_turnTaking');
  const noise = requireStats(target, others, 'fusion_noise');
  const diff = requireStats(target, others, 'fusion_diffuseness');

  const overrides: AcousticThresholdOverrides = {};
  if (turn) {
    overrides.babbleTurnTakingMin = Number(continuumLowerThreshold(turn.target, turn.others).toFixed(3));
  }
  if (noise) {
    overrides.babbleNoiseIntensityMin = Number(continuumLowerThreshold(noise.target, noise.others).toFixed(3));
  }
  if (diff) {
    overrides.babbleDiffusenessMin = Number(continuumLowerThreshold(diff.target, diff.others).toFixed(3));
  }
  return overrides;
}

const LABELS: Record<string, string> = {
  shoutingLoudnessThreshold: 'Geschrei Lautstärke',
  shoutingCrestFactorMin: 'Geschrei Crest',
  shoutingLoudnessWithCrestMin: 'Geschrei Lautst. + Crest',
  shoutingPeakDensityMin: 'Geschrei Peaks min',
  babbleDiffusenessMin: 'Gemurmel Diffusheit min',
  babbleNoiseIntensityMin: 'Gemurmel Rauschen min',
  babbleTurnTakingMin: 'Gemurmel Wechsel min',
  babbleLoudnessMin: 'Gemurmel Lautst. min',
  babbleLoudnessMax: 'Gemurmel Lautst. max',
  babblePeakDensityMax: 'Gemurmel Peaks max',
  yamnetBabbleTriggerMin: 'YAMNet Gemurmel min',
  yamnetShoutingTriggerMin: 'YAMNet Geschrei min',
  roomActivityNoiseMin: 'Raumaktivität Rauschen min',
  roomActivityLoudnessMin: 'Raumaktivität Lautst. min'
};

function describeDerivation(
  target: CategoryContinuum,
  others: CategoryContinuum[],
  overrides: AcousticThresholdOverrides
): string[] {
  const lines: string[] = [
    `Kontinuum „${target.tagLabel}" (${target.snapshotCount} Snapshots, min · Schwerpunkt · max):`
  ];

  const highlight: ContinuumMetricKey[] = [
    'fusion_loudness',
    'fusion_diffuseness',
    'fusion_noise',
    'fusion_turnTaking',
    'fusion_peaks'
  ];

  for (const key of highlight) {
    const s = target.metrics[key];
    if (!s) continue;
    const label = key.replace('fusion_', '').replace('yamnet_', 'YN ');
    lines.push(
      `  ${label}: ${s.min.toFixed(3)} · ${s.mean.toFixed(3)} · ${s.max.toFixed(3)}`
    );
  }

  if (others.length) {
    lines.push('Andere Kategorien (max der jeweiligen Kontinuua):');
    for (const other of others) {
      const loud = other.metrics.fusion_loudness;
      const diff = other.metrics.fusion_diffuseness;
      if (!loud && !diff) continue;
      lines.push(
        `  ${other.tagLabel}: Lautst. max ${loud?.max.toFixed(3) ?? '—'}, Diffusheit max ${diff?.max.toFixed(3) ?? '—'}`
      );
    }
  }

  lines.push('Neue Schwellen:');
  lines.push(...Object.entries(overrides).map(([key, value]) => `  ${LABELS[key] ?? key}: ${value.toFixed(3)}`));

  return lines;
}
